import { Stack } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { translate } from 'react-switch-lang';
import { Button, Form, FormFeedback, FormGroup, Input, Label } from 'reactstrap';
import { Practice } from '../../model/types';

interface PracticeFormProps {
  defaultValues?: Partial<Practice>;
  onSubmit: (data: Practice) => void;
  onCancel: () => void;
  isLoading?: boolean;
  t: (key: string) => string;
}

const PracticeForm = ({ defaultValues, onSubmit, onCancel, isLoading = false, t }: PracticeFormProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Practice>({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FormGroup>
        <Label>
          {t('practice.name')} <span className="text-danger">*</span>
        </Label>
        <Controller
          name="name"
          control={control}
          rules={{
            required: t('practice.field_required'),
            minLength: { value: 3, message: t('practice.min_length_3') },
            maxLength: { value: 100, message: t('practice.max_length_100') },
          }}
          render={({ field }) => (
            <Input
              {...field}
              type="text"
              invalid={!!errors.name}
              placeholder={t('practice.practice_name_placeholder')}
            />
          )}
        />
        {errors.name && <FormFeedback style={{ display: 'block' }}>{errors.name.message}</FormFeedback>}
      </FormGroup>

      <FormGroup>
        <Label>{t('practice.description')}</Label>
        <Controller
          name="description"
          control={control}
          rules={{
            maxLength: { value: 255, message: t('practice.max_length_255') },
          }}
          render={({ field }) => (
            <Input {...field} type="textarea" rows={3} placeholder={t('practice.practice_description_placeholder')} />
          )}
        />
        {errors.description && <FormFeedback style={{ display: 'block' }}>{errors.description.message}</FormFeedback>}
      </FormGroup>

      <FormGroup check className="mb-3">
        <Controller
          name="active"
          control={control}
          render={({ field }) => (
            <Input type="checkbox" checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
          )}
        />
        <Label check>{t('practice.active')}</Label>
      </FormGroup>

      <Stack direction="row" justifyContent="flex-end" spacing={1} mt={2}>
        <Button color="secondary" outline onClick={onCancel} disabled={isLoading}>
          {t('practice.cancel')}
        </Button>
        <Button color="primary" type="submit" disabled={isLoading}>
          {isLoading ? t('practice.saving') : t('practice.save')}
        </Button>
      </Stack>
    </Form>
  );
};

export default translate(PracticeForm);
